/* LiveStatsWidget — small public counter showing "🟢 X online · Y visitas hoje"
   Polls /api/analytics/public-stats every 10s. No auth.
*/
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, Users } from "lucide-react";
import { API } from "../App";

const fmtNum = (n) => Number(n || 0).toLocaleString("pt-BR");

export default function LiveStatsWidget({ variant = "floating", className = "" }) {
  const [stats, setStats] = useState({ online_now: 0, visits_today: 0 });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await fetch(`${API}/analytics/public-stats`);
        if (!r.ok) return;
        const d = await r.json();
        if (mounted) setStats(d);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  if (variant === "inline") {
    return (
      <div className={`inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs ${className}`} data-testid="live-stats-inline">
        <span className="flex items-center gap-1.5">
          <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
          </span>
          <strong className="text-emerald-300 tabular-nums">{fmtNum(stats.online_now)}</strong>
          <span className="text-zinc-400">online agora</span>
        </span>
        <span className="text-zinc-600">·</span>
        <span className="flex items-center gap-1.5 text-zinc-400">
          <Users className="w-3 h-3" />
          <strong className="text-white tabular-nums">{fmtNum(stats.visits_today)}</strong>
          <span>visitas hoje</span>
        </span>
        {stats.total_visits != null && (
          <>
            <span className="text-zinc-600">·</span>
            <span className="flex items-center gap-1.5 text-zinc-400" data-testid="live-stats-total">
              <strong className="text-amber-300 tabular-nums">{fmtNum(stats.total_visits)}</strong>
              <span>visitas no total</span>
            </span>
          </>
        )}
      </div>
    );
  }

  // Floating pill (default) — bottom-left, discreet
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        className={`fixed left-3 bottom-3 z-30 ${className}`}
        data-testid="live-stats-floating"
      >
        <div className="rounded-full bg-zinc-900/90 backdrop-blur-md border border-white/10 shadow-2xl px-3 py-1.5 flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1.5" title="Pessoas online agora">
            <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
            </span>
            <strong className="text-emerald-300 tabular-nums">{fmtNum(stats.online_now)}</strong>
            <span className="text-zinc-400 hidden sm:inline">online</span>
          </span>
          <span className="text-zinc-700">·</span>
          <span className="flex items-center gap-1.5" title="Visitas hoje">
            <Activity className="w-3 h-3 text-sky-400" />
            <strong className="text-white tabular-nums">{fmtNum(stats.visits_today)}</strong>
            <span className="text-zinc-400 hidden sm:inline">visitas</span>
          </span>
          {stats.total_visits != null && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="flex items-center gap-1.5" title="Total de visitas desde o início">
                <strong className="text-amber-300 tabular-nums">{fmtNum(stats.total_visits)}</strong>
                <span className="text-zinc-400 hidden sm:inline">no total</span>
              </span>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
